import moment from 'moment';
import { VEHICLE_USAGES } from '../../resources/constants';
import {
    INSTALLMENT_TYPE,
    INSTALLMENT_TYPES,
    LEAD_SOURCE,
    PAYMENT_STATUS_STATES,
    POLICY_DOCUMENT_TYPES,
    POLICY_REVIEW_STATES,
    COMPANY_TYPES
} from '../../resources/enums';
import { getPolicyDocumentTitleFromType } from '../../utils/miscFunctions';
import { IPolicySummary } from './IPolicy';
import { getVehicleProtectionFromKey } from '../../utils/miscFunctions';
import { ICustomerType } from '../../resources/interfaces';

export class PolicySummary {
    id: number;
    customerId: number;
    firstName: string;
    lastName: string;
    email: string;
    coverPeriod?: number;
    midReference?: number;
    insurerPolicyNumber?: number;
    stickerNumber?: number;
    phone: string;
    policyId: number;
    ref: string;
    reviewStage: string;
    protectionType: string;
    installmentType: string;
    vehicleUsage: string;
    leadSource: string;
    inceptionDate: string;
    expiresOn: string;
    createdOn: string;
    lastUpdated: string;
    outRightPremium: number;
    initialDeposit: number;
    installment: number;
    totalPremium: number;
    totalPayment: number;
    insurerId: number;
    insurerName: string;
    ownerId: number;
    ownerTeamName: string;
    managerId: number;
    managerName: string;
    vehicleId: number;
    make: string;
    model: string;
    registrationNumber: string;
    registrationYear: number;
    year: number;
    active: boolean;
    archived: boolean;
    countryId: number;
    createdDate: string;
    currency: string
    policyNumber: string;
    oldPolicyNumber: string;
    premiumPaidToInsurer: boolean;
    settlementReceivedByBroker: boolean;
    fleetPolicy?: boolean;
    customerType?: ICustomerType;
    businessName?: string;
    renewed: boolean;
    renewedPolicyNumber?:string

    constructor(Imodel: IPolicySummary) {
        this.id = Imodel.id;
        this.customerId = Imodel.customerId;
        this.firstName = Imodel.firstName;
        this.lastName = Imodel.lastName;
        this.email = Imodel.email;
        this.coverPeriod = Imodel.coverPeriod
        this.midReference = Imodel.midReference;
        this.insurerPolicyNumber = Imodel.insurerPolicyNumber;
        this.stickerNumber = Imodel.stickerNumber;
        this.phone = Imodel.phone;
        this.policyId = Imodel.policyId;
        this.ref = Imodel.policyNumber;
        this.reviewStage = Imodel.reviewStage;
        this.protectionType = getVehicleProtectionFromKey(Imodel.protectionType ?? '')?.title || '';
        this.installmentType = Imodel.installmentType;
        this.vehicleUsage = Imodel.vehicleUsage;
        this.leadSource = Imodel.leadSource;
        this.inceptionDate = Imodel.inceptionDate;
        this.expiresOn = Imodel.expiresOn;
        this.createdOn = Imodel.createdOn;
        this.lastUpdated = Imodel.lastUpdated;
        this.outRightPremium = Imodel.outRightPremium;
        this.initialDeposit = Imodel.initialDeposit;
        this.installment = Imodel.installment;
        this.totalPremium = Imodel.totalPremium;
        this.totalPayment = Imodel.totalPayment;
        this.insurerId = Imodel.insurerId;
        this.insurerName = Imodel.insurerName;
        this.ownerId = Imodel.ownerId;
        this.ownerTeamName = Imodel.ownerTeamName;
        this.managerId = Imodel.managerId;
        this.managerName = Imodel.managerName;
        this.vehicleId = Imodel.vehicleId;
        this.make = Imodel.make;
        this.model = Imodel.model;
        this.registrationNumber = Imodel.registrationNumber;
        this.registrationYear = Imodel.registrationYear;
        this.year = Imodel.year;
        this.active = Imodel.active;
        this.archived = Imodel.archived;
        this.countryId = Imodel.countryId;
        this.createdDate = Imodel.createdDate;
        this.currency = Imodel.currency;
        this.policyNumber = Imodel.policyNumber;
        this.oldPolicyNumber = Imodel.oldPolicyNumber;
        this.premiumPaidToInsurer = Imodel.premiumPaidToInsurer;
        this.settlementReceivedByBroker = Imodel.settlementReceivedByBroker;
        this.fleetPolicy = Imodel.fleetPolicy;
        this.customerType = Imodel?.customerType;
        this.businessName = Imodel?.businessName;
        this.renewed = Imodel?.renewed;
        this.renewedPolicyNumber = Imodel?.renewedPolicyNumber;
    }

    isCommercial = (): boolean => {
        return (
            this.vehicleUsage === VEHICLE_USAGES?.HIRING_CAR?.title ||
            this.vehicleUsage === VEHICLE_USAGES?.HAILING_SERVICE?.title ||
            this.vehicleUsage === VEHICLE_USAGES?.TAXI?.title
        );
    };

    isFullPayment(): boolean {
        return this.installmentType === INSTALLMENT_TYPES.FULL_PAYMENT;
    }

    isThirdParty(): boolean {
        return this.protectionType === 'Third Party';
    }

    getPaymentType = (): string => {
        return this.installmentType === INSTALLMENT_TYPE.FULL_PAYMENT
            ? 'Full Payment'
            : this.installmentType === INSTALLMENT_TYPE.WEEKLY_PAYMENT
                ? 'Weekly Installment'
                : 'Monthly Installment';
    };

    getStatus = (): string => {
        let status = 'Inactive';

        if (this.active) {
            status = 'Active';
        }

        return status;
    };

    isApproved = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.APPROVED;
    };

    isExpired = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.EXPIRED;
    };

    isOnHold = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.ON_HOLD;
    };

    isDueLater = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.DUE_LATER;
    };

    isInApproval = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.IN_APPROVAL;
    };

    isInReview = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.IN_REVIEW;
    };

    isDeclined = (): boolean => {
        return this.reviewStage === POLICY_REVIEW_STATES.DECLINED;
    };

    isAutoPolicy = (): boolean => {
        return this.make !== undefined && this.make !== null && this.model !== undefined && this.model !== null;
    };

    isComprehensive = (): boolean => {
        return (
            this.protectionType === 'COMPREHENSIVE_100' ||
            this.protectionType === 'COMPREHENSIVE_90' ||
            this.protectionType === 'COMPREHENSIVE_85'
        );
    };

    isRideSharing = (): boolean => {
        return this.ownerTeamName === COMPANY_TYPES.RIDE_SHARING;
    };

    isBolt = (): boolean => {
        return this.ownerTeamName === 'Bolt' || this?.leadSource === LEAD_SOURCE.BOLT_APP;
    };

    isWeeklyInstallment(): boolean {
        if (this.installmentType) {
            return (
                this.installmentType === 'TWELVE_WEEKS' ||
                this.installmentType.includes('WEEK')
            );
        }

        return false;
    }

    isDailyInstallment(): boolean {
        if (this.installmentType) {
            return this.installmentType === 'DAILY' || this.installmentType.includes('DAY');
        }
        return false;
    }

    isMonthlyInstallment(): boolean {
        if (this.installmentType) {
            return (
                this.installmentType === 'MONTHLY' || this.installmentType.includes('MONTH')
            );
        }
        return false;
    }


    isYango(): boolean {
        return this.ownerTeamName === 'Yango' || this?.leadSource === LEAD_SOURCE.YANGO_APP;
    }

    isMotorPolicy(): boolean {
        return this.vehicleUsage === 'MOTORCYCLE';
    }
}
